/**
 * Asset Store - manages loaded sprite assets with dynamic loading
 */

import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import { Assets, Texture as PixiTexture, Rectangle } from 'pixi.js';

/** Atlas entry for a single sprite frame */
export interface AtlasEntry {
    image: string;
    frame: { x: number; y: number; w: number; h: number };
    pivot?: { x: number; y: number };
}

/** Full tile atlas */
export interface TileAtlas {
    tiles: Record<string, AtlasEntry>;
    spritesheets: string[];
    /** Color key of each spritesheet image, as [r, g, b] (absent in old atlases) */
    colorKeys?: Record<string, [number, number, number]>;
}

/** Yield to the main thread to prevent freezing */
function yieldToMain(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
}

export const useAssetsStore = defineStore('assets', () => {
    // State
    // The atlas (88k entries) and everything holding PixiJS textures is kept
    // out of Vue's deep reactivity: proxied textures break PixiJS identity
    // checks and make every per-tile lookup go through a proxy. Components
    // react to `atlas` (set once) and `textureVersion` (bumped per spritesheet).
    const atlas = shallowRef<TileAtlas | null>(null);
    const textureVersion = ref(0);
    const error = ref<string | null>(null);
    const loadProgress = ref(0);
    const loadingStatus = ref<string>('');

    // Loads in flight - the palette and the map can load sprites concurrently
    const activeLoads = ref(0);
    const loading = computed(() => activeLoads.value > 0);

    const frameTextures = new Map<string, PixiTexture>();

    // Track which spritesheets are loaded or loading
    const loadedSpritesheets = new Set<string>();
    const loadingSpritesheets = new Map<string, Promise<boolean>>();

    // Pre-built indexes for fast lookup
    // image path -> list of [key, entry] pairs
    let spritesheetIndex = new Map<string, Array<[string, AtlasEntry]>>();
    // spriteId -> Set of image paths that contain frames for this sprite
    let spriteIdToImages = new Map<number, Set<string>>();
    // "spriteId_frame" -> atlas key (for fast texture lookup)
    let spriteFrameToKey = new Map<string, string>();
    // spriteId -> frame numbers, ascending
    let spriteIdToFrames = new Map<number, number[]>();
    // PAK name (lowercase) -> Set of sprite IDs in that PAK
    let pakToSpriteIds = new Map<string, Set<number>>();

    // Computed
    const isLoaded = computed(() => atlas.value !== null && !loading.value);

    // Actions

    /**
     * Load only the atlas metadata and build indexes incrementally
     */
    async function loadAtlas(): Promise<void> {
        if (loading.value || atlas.value) return;

        activeLoads.value++;
        error.value = null;
        loadingStatus.value = 'Fetching atlas...';

        try {
            const response = await fetch('assets/atlas.json');
            if (!response.ok) {
                throw new Error(`Failed to load atlas: ${response.statusText}`);
            }

            loadingStatus.value = 'Parsing atlas JSON...';
            await yieldToMain();

            const loadedAtlas: TileAtlas = await response.json();

            loadingStatus.value = 'Building indexes...';
            await yieldToMain();

            // Build indexes in chunks to avoid freezing
            const entries = Object.entries(loadedAtlas.tiles);
            const totalEntries = entries.length;
            const CHUNK_SIZE = 50000;

            const imgIndex = new Map<string, Array<[string, AtlasEntry]>>();
            const spriteIndex = new Map<number, Set<string>>();
            const frameIndex = new Map<string, string>();
            const framesIndex = new Map<number, number[]>();
            const pakIndex = new Map<string, Set<number>>();

            for (let i = 0; i < totalEntries; i += CHUNK_SIZE) {
                const chunk = entries.slice(i, i + CHUNK_SIZE);

                for (const [key, entry] of chunk) {
                    // Build spritesheet index
                    const list = imgIndex.get(entry.image);
                    if (list) {
                        list.push([key, entry]);
                    } else {
                        imgIndex.set(entry.image, [[key, entry]]);
                    }

                    // Extract PAK name, sprite ID and frame from key (pattern: pakname_spriteId_frame)
                    const match = key.match(/^(.+)_(\d+)_(\d+)$/);
                    if (match) {
                        const pakName = match[1].toLowerCase();
                        const spriteId = parseInt(match[2], 10);
                        const frame = parseInt(match[3], 10);

                        // Build sprite ID -> images index
                        const imgSet = spriteIndex.get(spriteId);
                        if (imgSet) {
                            imgSet.add(entry.image);
                        } else {
                            spriteIndex.set(spriteId, new Set([entry.image]));
                        }

                        // Build sprite_frame -> key index
                        frameIndex.set(`${spriteId}_${frame}`, key);

                        // Build sprite ID -> frames index
                        const frameList = framesIndex.get(spriteId);
                        if (frameList) {
                            frameList.push(frame);
                        } else {
                            framesIndex.set(spriteId, [frame]);
                        }

                        // Build PAK -> sprite IDs index
                        const pakSet = pakIndex.get(pakName);
                        if (pakSet) {
                            pakSet.add(spriteId);
                        } else {
                            pakIndex.set(pakName, new Set([spriteId]));
                        }
                    }
                }

                // Yield to main thread between chunks
                loadingStatus.value = `Building indexes... ${Math.min(i + CHUNK_SIZE, totalEntries)}/${totalEntries}`;
                await yieldToMain();
            }

            for (const frameList of framesIndex.values()) {
                frameList.sort((a, b) => a - b);
            }

            spritesheetIndex = imgIndex;
            spriteIdToImages = spriteIndex;
            spriteFrameToKey = frameIndex;
            spriteIdToFrames = framesIndex;
            pakToSpriteIds = pakIndex;

            // Publish last so anything reacting to the atlas sees the indexes
            atlas.value = loadedAtlas;

            console.log(`Atlas loaded: ${totalEntries} tiles, ${loadedAtlas.spritesheets.length} spritesheets, ${imgIndex.size} images, ${pakIndex.size} PAKs indexed`);
            loadingStatus.value = '';
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to load assets';
            console.error('Atlas loading error:', err);
        } finally {
            activeLoads.value--;
        }
    }

    /**
     * Load a specific spritesheet and create frame textures for it
     */
    function loadSpritesheet(imagePath: string): Promise<boolean> {
        if (loadedSpritesheets.has(imagePath)) return Promise.resolve(true);

        // Share the in-flight load if there is one
        let pending = loadingSpritesheets.get(imagePath);
        if (!pending) {
            pending = fetchSpritesheet(imagePath).finally(() => {
                loadingSpritesheets.delete(imagePath);
            });
            loadingSpritesheets.set(imagePath, pending);
        }
        return pending;
    }

    async function fetchSpritesheet(imagePath: string): Promise<boolean> {
        try {
            const fullPath = `assets/${imagePath}`;
            const texture = await Assets.load(fullPath) as PixiTexture;

            // Create frame textures using pre-built index
            const entries = spritesheetIndex.get(imagePath);
            if (entries) {
                const CHUNK_SIZE = 1000;
                for (let i = 0; i < entries.length; i += CHUNK_SIZE) {
                    const chunk = entries.slice(i, i + CHUNK_SIZE);

                    for (const [key, entry] of chunk) {
                        if (!frameTextures.has(key)) {
                            try {
                                const frameTexture = new PixiTexture({
                                    source: texture.source,
                                    frame: new Rectangle(
                                        entry.frame.x,
                                        entry.frame.y,
                                        entry.frame.w,
                                        entry.frame.h
                                    ),
                                });
                                frameTextures.set(key, frameTexture);
                            } catch (err) {
                                console.warn(`Failed to create frame: ${key}`, err);
                            }
                        }
                    }

                    // Yield between chunks if there are many frames
                    if (entries.length > CHUNK_SIZE) {
                        await yieldToMain();
                    }
                }
            }

            loadedSpritesheets.add(imagePath);
            textureVersion.value++;
            return true;
        } catch (err) {
            console.warn(`Failed to load spritesheet: ${imagePath}`, err);
            return false;
        }
    }

    /**
     * Find which spritesheets are needed for given sprite IDs (uses index - O(n) where n = spriteIds)
     */
    function getRequiredSpritesheets(spriteIds: Set<number>): Set<string> {
        const required = new Set<string>();
        for (const spriteId of spriteIds) {
            const images = spriteIdToImages.get(spriteId);
            if (images) {
                for (const img of images) {
                    required.add(img);
                }
            }
        }
        return required;
    }

    /**
     * Load all spritesheets required for a set of sprite IDs
     */
    async function loadSpritesForIds(spriteIds: Set<number>, onProgress?: (loaded: number, total: number) => void): Promise<void> {
        // Wait for atlas to be loaded first
        if (!atlas.value) return;

        const required = getRequiredSpritesheets(spriteIds);
        const toLoad = Array.from(required).filter(img => !loadedSpritesheets.has(img));

        if (toLoad.length === 0) return;

        console.log(`Loading ${toLoad.length} spritesheets for ${spriteIds.size} sprite IDs...`);
        activeLoads.value++;
        loadProgress.value = 0;

        try {
            // Load one at a time to avoid overwhelming the browser
            for (let i = 0; i < toLoad.length; i++) {
                loadingStatus.value = `Loading spritesheet ${i + 1}/${toLoad.length}...`;
                await loadSpritesheet(toLoad[i]);
                loadProgress.value = (i + 1) / toLoad.length;
                onProgress?.(i + 1, toLoad.length);
            }

            console.log(`Finished loading ${toLoad.length} spritesheets`);
            loadingStatus.value = '';
        } finally {
            activeLoads.value--;
        }
    }

    /**
     * Scan a map and load all required sprites
     */
    async function loadSpritesForMap(tiles: Array<{ tileSprite: number; objectSprite: number }>): Promise<void> {
        const spriteIds = new Set<number>();
        for (const tile of tiles) {
            if (tile.tileSprite >= 0) spriteIds.add(tile.tileSprite);
            if (tile.objectSprite > 0) spriteIds.add(tile.objectSprite);
        }
        await loadSpritesForIds(spriteIds);
    }

    /**
     * Get texture for a specific tile sprite and frame (uses index - O(1))
     */
    function getTileTexture(spriteId: number, frame: number): PixiTexture | undefined {
        const key = spriteFrameToKey.get(`${spriteId}_${frame}`);
        if (key) {
            return frameTextures.get(key);
        }
        return undefined;
    }

    /**
     * Get atlas entry for a specific sprite and frame (uses index - O(1))
     */
    function getAtlasEntry(spriteId: number, frame: number): AtlasEntry | null {
        if (!atlas.value) return null;
        const key = spriteFrameToKey.get(`${spriteId}_${frame}`);
        if (key) {
            return atlas.value.tiles[key] ?? null;
        }
        return null;
    }

    /**
     * Color the converter made transparent in a sprite's sheet, as 0xRRGGBB.
     * The game draws base tiles opaque, so the renderer paints this behind them.
     */
    function getColorKey(spriteId: number, frame: number): number | null {
        const entry = getAtlasEntry(spriteId, frame);
        const key = entry ? atlas.value?.colorKeys?.[entry.image] : undefined;
        return key ? (key[0] << 16) | (key[1] << 8) | key[2] : null;
    }

    /**
     * Get all frames for a sprite ID, ascending
     */
    function getFramesForSprite(spriteId: number): number[] {
        if (!atlas.value) return [];
        return spriteIdToFrames.get(spriteId) ?? [];
    }

    /**
     * Get all sprite IDs for a PAK name (O(1) lookup)
     */
    function getSpritesForPak(pakName: string): number[] {
        if (!atlas.value) return [];
        const sprites = pakToSpriteIds.get(pakName.toLowerCase());
        if (sprites) {
            return Array.from(sprites).sort((a, b) => a - b);
        }
        return [];
    }

    /**
     * Get all PAK names
     */
    function getPakNames(): string[] {
        // Reads the atlas ref so computeds re-run once the indexes exist
        if (!atlas.value) return [];
        return Array.from(pakToSpriteIds.keys()).sort();
    }

    return {
        // State
        atlas,
        textureVersion,
        error,
        loadProgress,
        loadingStatus,
        // Computed
        loading,
        isLoaded,
        // Actions
        loadAtlas,
        loadSpritesheet,
        loadSpritesForIds,
        loadSpritesForMap,
        getTileTexture,
        getAtlasEntry,
        getColorKey,
        getFramesForSprite,
        getSpritesForPak,
        getPakNames,
    };
});
